import { palette } from '../constants/colors';

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    v !== null && typeof v === 'object' && !Array.isArray(v);

export const deepMerge = <T = any>(base: any, override: any): T => {
    if (!isPlainObject(base)) return (override === undefined ? base : override) as T;
    if (!isPlainObject(override)) return (override === undefined ? base : override) as T;
    const out: Record<string, any> = { ...base };
    for (const key of Object.keys(override)) {
        out[key] = deepMerge(base[key], override[key]);
    }
    return out as T;
};

export const hexToRgba = (hex: string, alpha = 1): string => {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getChartColors = (count: number, alpha = 1): string[] => {
    const base = palette.chart;
    const out: string[] = [];
    for (let i = 0; i < count; i += 1) {
        const hex = base[i % base.length];
        out.push(alpha === 1 ? hex : hexToRgba(hex, alpha));
    }
    return out;
};

export const getChartColorAt = (index: number, alpha = 1): string => {
    const base = palette.chart;
    const hex = base[((index % base.length) + base.length) % base.length];
    return alpha === 1 ? hex : hexToRgba(hex, alpha);
};

export interface FormatNumberOptions {
    decimals?: number;
    compact?: boolean;
    locale?: string;
}

export const formatNumber = (
    value: number | string | null | undefined,
    { decimals, compact = false, locale = 'en-US' }: FormatNumberOptions = {},
): string => {
    if (value == null || Number.isNaN(Number(value))) return '';
    const n = Number(value);
    const opts: Intl.NumberFormatOptions = {
        notation: compact ? 'compact' : 'standard',
        maximumFractionDigits: decimals ?? (compact ? 1 : 2),
        minimumFractionDigits: decimals != null ? decimals : 0,
    };
    return new Intl.NumberFormat(locale, opts).format(n);
};

export interface FormatPercentOptions {
    decimals?: number;
    locale?: string;
}

export const formatPercent = (
    value: number | string | null | undefined,
    { decimals = 1, locale = 'en-US' }: FormatPercentOptions = {},
): string => {
    if (value == null || Number.isNaN(Number(value))) return '';
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
    }).format(Number(value));
};

export interface FormatCurrencyOptions {
    currency?: string;
    locale?: string;
    compact?: boolean;
}

export const formatCurrency = (
    value: number | string | null | undefined,
    { currency = 'USD', locale = 'en-US', compact = false }: FormatCurrencyOptions = {},
): string => {
    if (value == null || Number.isNaN(Number(value))) return '';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        notation: compact ? 'compact' : 'standard',
        maximumFractionDigits: compact ? 1 : 2,
    }).format(Number(value));
};

export type DatePresetVariant =
    | 'short'
    | 'long'
    | 'monthYear'
    | 'dayMonth'
    | 'time'
    | 'dateTime';

export interface FormatDateOptions {
    variant?: DatePresetVariant;
    locale?: string;
}

export const formatDate = (
    value: Date | string | number | null | undefined,
    { variant = 'short', locale = 'en-US' }: FormatDateOptions = {},
): string => {
    if (value == null || value === '') return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const presets: Record<DatePresetVariant, Intl.DateTimeFormatOptions> = {
        short: { year: 'numeric', month: 'short', day: 'numeric' },
        long: { year: 'numeric', month: 'long', day: 'numeric' },
        monthYear: { year: 'numeric', month: 'short' },
        dayMonth: { month: 'short', day: 'numeric' },
        time: { hour: '2-digit', minute: '2-digit' },
        dateTime: { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    };
    return new Intl.DateTimeFormat(locale, presets[variant] ?? presets.short).format(d);
};

export const calculatePercentage = (
    part: number | string | null | undefined,
    total: number | string | null | undefined,
    { decimals = 1 }: { decimals?: number } = {},
): number => {
    const t = Number(total);
    if (!t) return 0;
    return Number(((Number(part) / t) * 100).toFixed(decimals));
};

export interface ChartDatasetLike {
    label?: string;
    data: any[];
    [key: string]: any;
}

export interface ChartDataLike {
    labels?: any[];
    datasets: ChartDatasetLike[];
}

export interface TransformRowsToDatasetOptions {
    labelKey: string;
    valueKey: string;
    label?: string;
    color?: string;
    alpha?: number;
}

export const transformRowsToDataset = (
    rows: any[],
    { labelKey, valueKey, label, color, alpha = 1 }: TransformRowsToDatasetOptions,
): ChartDataLike => {
    if (!Array.isArray(rows) || !labelKey || !valueKey) {
        return { labels: [], datasets: [] };
    }
    const labels = rows.map((r) => r?.[labelKey]);
    const data = rows.map((r) => Number(r?.[valueKey] ?? 0));
    const baseColor = color ?? getChartColorAt(0);
    return {
        labels,
        datasets: [
            {
                label: label ?? valueKey,
                data,
                backgroundColor: alpha === 1 ? baseColor : hexToRgba(baseColor, alpha),
                borderColor: baseColor,
                borderWidth: 2,
            },
        ],
    };
};

export interface TransformRowsToCategoricalSeriesOptions {
    labelKey: string;
    valueKey: string;
    seriesKey: string;
    alpha?: number;
}

export const transformRowsToCategoricalSeries = (
    rows: any[],
    { labelKey, valueKey, seriesKey, alpha = 1 }: TransformRowsToCategoricalSeriesOptions,
): ChartDataLike => {
    if (!Array.isArray(rows) || !labelKey || !valueKey || !seriesKey) {
        return { labels: [], datasets: [] };
    }
    const labelSet: any[] = [];
    const seriesSet: any[] = [];
    const indexLabel = new Map<any, number>();
    const indexSeries = new Map<any, number>();
    for (const row of rows) {
        const lbl = row?.[labelKey];
        const ser = row?.[seriesKey];
        if (!indexLabel.has(lbl)) {
            indexLabel.set(lbl, labelSet.length);
            labelSet.push(lbl);
        }
        if (!indexSeries.has(ser)) {
            indexSeries.set(ser, seriesSet.length);
            seriesSet.push(ser);
        }
    }
    const matrix: number[][] = seriesSet.map(() => labelSet.map(() => 0));
    for (const row of rows) {
        const li = indexLabel.get(row?.[labelKey]);
        const si = indexSeries.get(row?.[seriesKey]);
        if (li != null && si != null) {
            matrix[si][li] = Number(row?.[valueKey] ?? 0);
        }
    }
    return {
        labels: labelSet,
        datasets: seriesSet.map((name, i) => ({
            label: String(name),
            data: matrix[i],
            backgroundColor: getChartColorAt(i, alpha),
            borderColor: getChartColorAt(i),
            borderWidth: 2,
        })),
    };
};

export interface BuildTooltipLabelOptions {
    valueFormatter?: (raw: any) => string;
    suffix?: string;
    prefix?: string;
}

export const buildTooltipLabel = ({
    valueFormatter,
    suffix = '',
    prefix = '',
}: BuildTooltipLabelOptions = {}) => (ctx: any): string => {
    const raw = ctx.parsed?.y ?? ctx.parsed ?? ctx.raw;
    const formatted = valueFormatter ? valueFormatter(raw) : formatNumber(raw);
    const dsLabel = ctx.dataset?.label;
    const pieces: string[] = [];
    if (dsLabel) pieces.push(dsLabel);
    pieces.push(`${prefix}${formatted}${suffix}`);
    return pieces.join(': ');
};

export interface LegendItem {
    label: string;
    color: string;
    value?: any;
}

export interface BuildLegendItemsOptions {
    colors?: string[];
    dataset?: any[];
}

export const buildLegendItems = (
    labels: any[],
    { colors, dataset }: BuildLegendItemsOptions = {},
): LegendItem[] => {
    if (!Array.isArray(labels)) return [];
    const colorList = colors ?? getChartColors(labels.length);
    return labels.map((label, i) => ({
        label: String(label),
        color: colorList[i % colorList.length],
        value: dataset?.[i],
    }));
};

export const isMissingColor = (c: unknown): boolean => c == null || c === '';

export const seriesColorForIndex = (i: number): string => {
    if (i === 0) return palette.secondary;
    if (i === 1) return palette.accent;
    return getChartColorAt(i);
};

export interface ResponsiveSize {
    font: number;
    ticks: number;
}

export interface GetResponsiveSizeOptions {
    compact?: ResponsiveSize;
    regular?: ResponsiveSize;
    wide?: ResponsiveSize;
    breakpoints?: [number, number];
}

export const getResponsiveSize = (
    width: number | null | undefined,
    {
        compact = { font: 9, ticks: 4 },
        regular = { font: 11, ticks: 6 },
        wide = { font: 12, ticks: 8 },
        breakpoints = [360, 720],
    }: GetResponsiveSizeOptions = {},
): ResponsiveSize => {
    if (width == null) return regular;
    if (width < breakpoints[0]) return compact;
    if (width < breakpoints[1]) return regular;
    return wide;
};
